import { ORCHESTRATION_PROVIDER, OrchestrationBQAuthType } from '@common/Fabric/types/orchestration';
import { useUnifiedFabricContext } from '@common/Fabric/UnifiedFabrics/context';
import { useVerticalAppFlag } from '@common/VerticalApps/VerticalAppContext';
import { useProviderForm } from '@pages/Fabric/common/ProviderFormContext';
import { Card } from '@pages/Fabric/common/styled';
import { UploaderController } from '@pages/Fabric/common/UploaderController';
import { FabricTokens } from '@pages/Fabric/tokens';
import { UnifiedBigQueryFormData } from '@pages/Fabric/types';
import { useUnifiedBigQueryTestConnection } from '@pages/Fabric/unifiedFabrics/bigquery/useUnifiedBigQueryTestConnection';
import { OrchOAuthAppRegistration } from '@pages/Fabric/unifiedFabrics/OrchOAuthAppRegistration';
import { theme } from '@prophecy/theme';
import { Input, RadioGroup, renderRequiredLabel, Select, Stack, Text, useController } from '@prophecy/ui-v3';
import { Controller, UseFormReturn } from 'react-hook-form';

import { AuthStateCard } from '../../../UserSettings/components/SetupEnvironment/AuthCard';

export const UnifiedBigQueryForm = () => {
  const { formContext, fabricId, isCreateMode, readonly, fabricAuthState, orchestrationProviderInfo } =
    useProviderForm();
  const { dataplaneUrls } = orchestrationProviderInfo;
  const isVerticalApp = useVerticalAppFlag();
  const form = formContext as unknown as UseFormReturn<UnifiedBigQueryFormData>;
  const {
    control,
    watch,
    formState: { errors }
  } = form;

  const bqAppRegistrations = useUnifiedFabricContext((state) => state.bigQueryAppRegistrations);
  const bqSupportedProviders = useUnifiedFabricContext((state) => state.bigQuerySupportedProviders);

  useUnifiedBigQueryTestConnection(formContext, bqSupportedProviders, fabricId);

  const { field: isDefaultRegistrationField } = useController({
    name: 'unifiedFabricData.oAuthDetails.isDefaultRegistration',
    control
  });

  const authType = watch('unifiedFabricData.authType') ?? OrchestrationBQAuthType.PrivateKey;
  const isUpdateFlow = !isCreateMode && Boolean(fabricId);

  return (
    <Stack gap={FabricTokens.Form.sectionGap}>
      <Card gap={FabricTokens.Form.fieldGap}>
        <Text level='md'>Configure your BigQuery warehouse details</Text>

        <Controller
          control={control}
          name='unifiedFabricData.projectId'
          rules={{ required: 'Project ID is required' }}
          render={({ field }) => (
            <Input
              label='Project ID'
              required
              disabled={readonly}
              placeholder='Enter Google Cloud Project ID'
              {...field}
              data-test-id='bigquery-project-id-input'
            />
          )}
        />

        <Controller
          control={control}
          name='unifiedFabricData.dataset'
          rules={{ required: 'Dataset is required' }}
          render={({ field }) => (
            <Input
              label='Dataset'
              required
              disabled={readonly}
              placeholder='Enter Dataset ID'
              {...field}
              data-test-id='bigquery-dataset-input'
            />
          )}
        />

        <Controller
          control={control}
          name='unifiedFabricData.bucketName'
          render={({ field }) => (
            <Input
              label={
                <>
                  Bucket Name{' '}
                  <Text level='sm' tone={theme.colors.gray500}>
                    (Improves writing to BigQuery performance)
                  </Text>
                </>
              }
              disabled={readonly}
              placeholder='Enter Bucket Name'
              {...field}
              data-test-id='bigquery-bucket-name-input'
            />
          )}
        />

        <Controller
          control={control}
          name='unifiedFabricData.kmsKey'
          render={({ field }) => (
            <Input
              label='KMS Key'
              disabled={readonly}
              placeholder='Enter cloud KMS key for CMEK encryption'
              {...field}
              data-test-id='bigquery-kms-key-input'
            />
          )}
        />

        {!isVerticalApp && isCreateMode && dataplaneUrls.length > 0 && (
          <Controller
            control={control}
            name='unifiedFabricData.dataplaneURL'
            rules={{ required: 'Prophecy Dataplane URL is required' }}
            render={({ field }) => (
              <Select
                {...field}
                label='Prophecy Dataplane URL'
                options={dataplaneUrls}
                disabled={!isCreateMode || dataplaneUrls.length === 1}
                data-test-id='bigquery-form-dataplane-url-select'
              />
            )}
          />
        )}
      </Card>

      <Card gap={theme.spaces.x32}>
        <Text level='md'>BigQuery Authentication</Text>

        <Stack gap={theme.spaces.x20}>
          <Stack gap={theme.spaces.x12}>
            {renderRequiredLabel({ label: 'Authentication method', required: true })}

            <Controller
              control={control}
              name='unifiedFabricData.authType'
              render={({ field }) => (
                <RadioGroup
                  disabled={readonly}
                  value={field.value}
                  gap={theme.spaces.x32}
                  onChange={field.onChange}
                  orientation='horizontal'
                  options={[
                    {
                      label: 'Private Key',
                      value: OrchestrationBQAuthType.PrivateKey,
                      key: 'bigquery-auth-private-key-option'
                    },
                    {
                      label: 'OAuth',
                      value: OrchestrationBQAuthType.OAuth,
                      key: 'bigquery-auth-oauth-option',
                      disabled: true,
                      tooltip: 'OAuth is not supported for BigQuery'
                    }
                  ]}
                />
              )}
            />
          </Stack>
        </Stack>

        {authType === OrchestrationBQAuthType.PrivateKey && (
          <Stack gap={theme.spaces.x12}>
            {renderRequiredLabel({ label: 'Service Account Key', required: !isUpdateFlow })}
            <UploaderController
              label='Service Account Key (JSON)'
              accept={{ 'application/json': ['.json'] }}
              control={control}
              name='unifiedFabricData.serviceAccountKey'
              rules={{ required: !isUpdateFlow && 'Service Account Key is required' }}
              helpText={errors.unifiedFabricData?.serviceAccountKey?.message}
              data-test-id='bigquery-service-account-key-input'
              unregisterOnUnmount
            />
          </Stack>
        )}

        {/* Always mount app registration selector; visibility is handled inside the component (same as Databricks) */}
        <OrchOAuthAppRegistration
          appRegistrations={bqAppRegistrations}
          formContext={formContext}
          onIsDefaultChange={(isDefault) => {
            isDefaultRegistrationField.onChange(isDefault);
          }}
          provider={ORCHESTRATION_PROVIDER.BigQuery}
        />

        <AuthStateCard authState={fabricAuthState ?? null} provider='BigQuery' />
      </Card>
    </Stack>
  );
};
